import { useMemo } from 'react';

import { useLazyQuery } from '@apollo/client/react';

import { Types } from '@/types';

declare global {
  interface Window {
    Tally?: {
      openPopup: (formId: string, options: any) => void;
    };
  }
}

import { userSession } from '../../helpers';
import { CrispService } from '../crisp';

import { getAuthClient } from './auth-client';
import { GetCurrentUserInfoDocument } from './useCurrentUserInfo.query.generated';

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(
      Buffer.from(
        token.replace('Bearer ', '').split('.')[1],
        'base64'
      ).toString()
    );
    const expirationTime = payload.exp * 1000; // Convertir en millisecondes
    return Date.now() >= expirationTime;
  } catch (_error) {
    return true; // En cas d'erreur de parsing, on considère le token comme expiré
  }
};

export const useCurrentUserInfo = () => {
  const [getUserInfo] = useLazyQuery(GetCurrentUserInfoDocument);

  return useMemo(() => {
    const getCurrentUserInfo = async (): Promise<{
      info: Types.Users_User;
      jwtToken: string;
    }> => {
      const { fetchAuthSession } = await getAuthClient();

      // Récupère le jwt token
      const session = await fetchAuthSession();
      const jwtToken = session.tokens?.idToken?.toString();

      const originalJwtToken = userSession.getToken();

      // Si pas de nouveau token et pas d'ancien token valide, on lance une erreur
      if (
        !jwtToken &&
        (!originalJwtToken ||
          (originalJwtToken && isTokenExpired(originalJwtToken)))
      ) {
        userSession.reset(); // On nettoie la session si le token est expiré
        throw new Error("Can't retrieve jwt token");
      }

      // Si on a un nouveau token, on l'utilise, sinon on garde l'ancien s'il est valide
      const tokenToUse =
        jwtToken ||
        (originalJwtToken && !isTokenExpired(originalJwtToken)
          ? originalJwtToken.replace('Bearer ', '')
          : null);

      if (!tokenToUse) {
        userSession.reset();
        throw new Error('No valid token available');
      }

      userSession.setToken(tokenToUse ? `Bearer ${tokenToUse}` : '');

      // Décode le payload du JWT token
      const payload = JSON.parse(
        Buffer.from(tokenToUse.split('.')[1], 'base64').toString()
      );

      const hasuraClaims = JSON.parse(payload['https://hasura.io/jwt/claims']);
      const sessionId = hasuraClaims['x-hasura-session-id'] || '';
      userSession.setSessionId(sessionId);

      // Récupère les infos utilisateur
      const result = await getUserInfo();
      const user = result.data?.users_user?.[0];

      if (!user) {
        userSession.reset();
        throw new Error("Can't retrieve user");
      }

      if (user) {
        CrispService.setUserInfo(user);

        // Tally permet de réaliser des enquêtes. Changer l'id par l'id de l'enquête.
        // Ouvre la popup Tally avec l'email de l'utilisateur en hiddenFields.
        // if (typeof window !== 'undefined' && window.Tally) {
        //   window.Tally.openPopup('woKQEx', {
        //     layout: 'modal',
        //     width: 600,
        //     emoji: {
        //       text: '👋',
        //       animation: 'wave',
        //     },
        //     doNotShowAfterSubmit: true,
        //     hiddenFields: {
        //       email: user?.email,
        //     },
        //   });
        // }
      }

      userSession.setIsSuperUser(user.is_superuser);

      return {
        info: user,
        jwtToken: tokenToUse,
      };
    };

    return { getCurrentUserInfo };
  }, []);
};
